import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Routes, Route } from "react-router-dom";
import apiClient from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { LayoutDashboard, Package, ShoppingCart, Wallet, Store, ArrowLeft, Plus, ExternalLink, Save } from "lucide-react";

export const SellerGuard = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/login?next=/seller"); return; }
    apiClient.get("/sellers/me").then((r) => {
      if (r.data.status !== "approved") navigate("/seller/status");
      else setSeller(r.data);
    }).catch(() => navigate("/sell")).finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink-muted">Loading…</div>;
  if (!seller) return null;
  return children(seller);
};

const NAV = [
  { to: "/seller", end: true, icon: LayoutDashboard, label_en: "Overview", label_nl: "Overzicht" },
  { to: "/seller/products", icon: Package, label_en: "Products", label_nl: "Producten" },
  { to: "/seller/orders", icon: ShoppingCart, label_en: "Orders", label_nl: "Bestellingen" },
  { to: "/seller/earnings", icon: Wallet, label_en: "Earnings", label_nl: "Verdiensten" },
  { to: "/seller/profile", icon: Store, label_en: "Profile", label_nl: "Profiel" },
];

const SellerLayout = ({ children, seller }) => {
  const { lang } = useI18n();
  return (
    <div className="min-h-screen bg-bone flex">
      <aside className="w-60 bg-white border-r border-black/10 px-5 py-6 hidden md:flex flex-col">
        <div className="font-label text-terracotta text-[10px]">SELLER</div>
        <div className="font-display text-xl truncate" title={seller.company_name}>{seller.company_name}</div>
        <div className="text-[11px] text-ink-muted truncate">{seller.contact_email}</div>
        <nav className="mt-6 space-y-1 flex-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`seller-nav-${n.to.split("/").pop() || "home"}`} className={({ isActive }) => `flex items-center gap-2.5 text-sm px-3 py-2 ${isActive ? "bg-ink text-white" : "hover:bg-bone"}`}>
              <n.icon className="w-4 h-4" /> {lang === "nl" ? n.label_nl : n.label_en}
            </NavLink>
          ))}
        </nav>
        <Link to="/" className="mt-4 text-xs font-label text-ink-muted hover:text-ink inline-flex items-center gap-1.5"><ArrowLeft className="w-3 h-3" /> {lang === "nl" ? "Terug naar winkel" : "Back to shop"}</Link>
        <a href={`/store/${seller.slug}`} target="_blank" rel="noreferrer" className="mt-1 text-xs font-label text-terracotta inline-flex items-center gap-1.5"><ExternalLink className="w-3 h-3" /> {lang === "nl" ? "Bekijk je winkel" : "View your store"}</a>
      </aside>
      <main className="flex-1 px-6 lg:px-10 py-8 min-w-0">{children}</main>
    </div>
  );
};

// ----- Pages -----
const SellerOverview = ({ seller }) => {
  const { lang } = useI18n();
  const [stats, setStats] = useState({ products: 0, earnings: null });

  useEffect(() => {
    Promise.all([
      apiClient.get("/seller/products").then((r) => setStats((s) => ({ ...s, products: r.data.length }))),
      apiClient.get("/seller/earnings").then((r) => setStats((s) => ({ ...s, earnings: r.data }))),
    ]).catch(() => {});
  }, []);

  return (
    <div data-testid="seller-overview">
      <h1 className="font-display text-4xl tracking-tighter font-light">{lang === "nl" ? `Welkom, ${seller.contact_person}` : `Welcome, ${seller.contact_person}`}</h1>
      <p className="text-ink-muted mt-1">{lang === "nl" ? "Snel overzicht van je activiteit." : "Quick overview of your activity."}</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Card label={lang === "nl" ? "Producten" : "Products"} value={stats.products} />
        <Card label={lang === "nl" ? "Bruto verkoop" : "Gross sales"} value={`€${(stats.earnings?.gross || 0).toFixed(2)}`} />
        <Card label={lang === "nl" ? "Commissie" : "Commission"} value={`€${(stats.earnings?.commission || 0).toFixed(2)}`} />
        <Card label={lang === "nl" ? "Uitbetaling totaal" : "Payout total"} value={`€${(stats.earnings?.payout_total || 0).toFixed(2)}`} />
      </div>
      <div className="mt-10 flex gap-3 flex-wrap">
        <Link to="/seller/products/new" className="btn-primary" data-testid="seller-add-product-cta"><Plus className="w-4 h-4" /> {lang === "nl" ? "Product toevoegen" : "Add product"}</Link>
        <Link to="/seller/orders" className="btn-secondary">{lang === "nl" ? "Bestellingen bekijken" : "View orders"}</Link>
      </div>
    </div>
  );
};
const Card = ({ label, value }) => (
  <div className="border border-black/10 bg-white p-5">
    <div className="text-xs font-label text-ink-muted">{label}</div>
    <div className="font-display text-3xl tracking-tighter mt-1">{value}</div>
  </div>
);

const SellerProducts = () => {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const load = () => apiClient.get("/seller/products").then((r) => setItems(r.data));
  useEffect(load, []);
  const del = async (pid) => {
    if (!confirm(lang === "nl" ? "Weet je het zeker?" : "Are you sure?")) return;
    try { await apiClient.delete(`/seller/products/${pid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="seller-products-list">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-4xl tracking-tighter font-light">{lang === "nl" ? "Producten" : "Products"}</h1>
        <Link to="/seller/products/new" className="btn-primary text-sm" data-testid="seller-product-new"><Plus className="w-4 h-4" /> {lang === "nl" ? "Nieuw" : "New"}</Link>
      </div>
      <div className="border border-black/10 bg-white divide-y divide-black/5">
        {items.length === 0 && <div className="p-8 text-center text-ink-muted text-sm">{lang === "nl" ? "Nog geen producten." : "No products yet."}</div>}
        {items.map((p) => (
          <div key={p.product_id} className="p-4 flex items-center gap-4">
            <div className="w-12 h-14 bg-secondary-warm overflow-hidden shrink-0"><img src={p.images?.[0]} alt="" className="w-full h-full object-cover" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display truncate">{lang === "nl" ? p.name_nl : p.name_en}</div>
              <div className="text-xs text-ink-muted">{p.sku} · €{p.price.toFixed(2)} · {lang === "nl" ? "Voorraad" : "Stock"}: {p.stock} · {p.status}</div>
            </div>
            <Link to={`/seller/products/${p.product_id}`} className="btn-secondary text-xs" data-testid={`edit-product-${p.product_id}`}>{lang === "nl" ? "Bewerken" : "Edit"}</Link>
            <button onClick={() => del(p.product_id)} className="text-xs px-3 py-2 border border-rose-300 text-rose-600 hover:bg-rose-50">{lang === "nl" ? "Verwijderen" : "Delete"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const emptyP = { sku: "", slug: "", name_en: "", name_nl: "", description_en: "", description_nl: "", price: 0, currency: "EUR", category: "home-living", images: [""], stock: 0, status: "active", vat_rate: null };

const SellerProductEdit = () => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const pid = window.location.pathname.split("/").pop();
  const isNew = pid === "new";
  const [form, setForm] = useState(emptyP);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    apiClient.get("/seller/products").then((r) => {
      const found = r.data.find((p) => p.product_id === pid);
      if (found) setForm({ ...emptyP, ...found, images: found.images?.length ? found.images : [""] });
    });
  }, [pid, isNew]);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price), stock: parseInt(form.stock),
        images: (form.images || []).filter(Boolean),
        vat_rate: form.vat_rate === "" || form.vat_rate == null ? null : parseFloat(form.vat_rate),
      };
      if (isNew) await apiClient.post("/seller/products", payload);
      else await apiClient.put(`/seller/products/${pid}`, payload);
      toast.success(lang === "nl" ? "Opgeslagen" : "Saved");
      navigate("/seller/products");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-5" data-testid="seller-product-edit">
      <Link to="/seller/products" className="font-label text-ink-muted hover:text-ink text-xs">← {lang === "nl" ? "Producten" : "Products"}</Link>
      <h1 className="font-display text-4xl tracking-tighter font-light">{isNew ? (lang === "nl" ? "Nieuw product" : "New product") : (lang === "nl" ? "Bewerk product" : "Edit product")}</h1>
      <div className="grid md:grid-cols-2 gap-3">
        <F label="SKU *" value={form.sku} onChange={(v) => upd("sku", v)} required />
        <F label="Slug *" value={form.slug} onChange={(v) => upd("slug", v)} required />
        <F label="Name EN *" value={form.name_en} onChange={(v) => upd("name_en", v)} required testid="sp-name-en" />
        <F label="Naam NL *" value={form.name_nl} onChange={(v) => upd("name_nl", v)} required testid="sp-name-nl" />
        <F label={lang === "nl" ? "Prijs (€) *" : "Price (€) *"} type="number" step="0.01" value={form.price} onChange={(v) => upd("price", v)} required testid="sp-price" />
        <F label={lang === "nl" ? "Voorraad *" : "Stock *"} type="number" value={form.stock} onChange={(v) => upd("stock", v)} required testid="sp-stock" />
        <F label="Category" value={form.category} onChange={(v) => upd("category", v)} />
        <F label="Image URL" value={form.images?.[0] || ""} onChange={(v) => upd("images", [v])} />
      </div>
      <Textarea label="Description EN" value={form.description_en} onChange={(v) => upd("description_en", v)} />
      <Textarea label="Beschrijving NL" value={form.description_nl} onChange={(v) => upd("description_nl", v)} />
      <button type="submit" disabled={busy} className="btn-primary" data-testid="sp-save"><Save className="w-4 h-4" /> {busy ? "…" : (lang === "nl" ? "Opslaan" : "Save")}</button>
    </form>
  );
};

const SellerOrders = () => {
  const { lang } = useI18n();
  const [orders, setOrders] = useState([]);
  useEffect(() => { apiClient.get("/seller/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div data-testid="seller-orders-list">
      <h1 className="font-display text-4xl tracking-tighter font-light">{lang === "nl" ? "Mijn bestellingen" : "My orders"}</h1>
      <div className="mt-6 border border-black/10 bg-white divide-y divide-black/5">
        {orders.length === 0 && <div className="p-8 text-center text-ink-muted text-sm">{lang === "nl" ? "Nog geen bestellingen." : "No orders yet."}</div>}
        {orders.map((o) => (
          <div key={o.order_id} className="p-4">
            <div className="flex justify-between text-sm">
              <div><strong>{o.order_id}</strong> · {o.shipping?.full_name} · {(o.created_at || "").slice(0, 10)}</div>
              <div>€{o.own_gross.toFixed(2)} ({lang === "nl" ? "uitbetaling" : "payout"}: <strong>€{o.own_payout.toFixed(2)}</strong>)</div>
            </div>
            <div className="mt-2 space-y-1">
              {o.lines.map((ln) => (
                <div key={ln.product_id} className="text-xs text-ink-muted flex justify-between">
                  <span>{ln.qty} × {lang === "nl" ? ln.name_nl : ln.name_en}</span>
                  <span>{ln.fulfilment_status || "pending"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SellerEarnings = () => {
  const { lang } = useI18n();
  const [e, setE] = useState(null);
  useEffect(() => { apiClient.get("/seller/earnings").then((r) => setE(r.data)); }, []);
  if (!e) return <div className="text-ink-muted">Loading…</div>;
  return (
    <div data-testid="seller-earnings-page">
      <h1 className="font-display text-4xl tracking-tighter font-light">{lang === "nl" ? "Verdiensten" : "Earnings"}</h1>
      <p className="text-ink-muted mt-1 text-sm">{lang === "nl" ? "Tijdens deze fase zijn uitbetalingen handmatig (Net 30)." : "During this phase payouts are manual (Net 30)."}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        <Card label={lang === "nl" ? "Bruto verkoop" : "Gross sales"} value={`€${e.gross.toFixed(2)}`} />
        <Card label={lang === "nl" ? "Commissie SnapShop" : "SnapShop commission"} value={`−€${e.commission.toFixed(2)}`} />
        <Card label={lang === "nl" ? "Jouw uitbetaling" : "Your payout"} value={`€${e.payout_total.toFixed(2)}`} />
      </div>
    </div>
  );
};

const SellerProfile = ({ seller }) => {
  const { lang } = useI18n();
  const [form, setForm] = useState({
    bio_en: seller.bio_en || "", bio_nl: seller.bio_nl || "",
    logo_url: seller.logo_url || "", banner_url: seller.banner_url || "",
    website_url: seller.website_url || "", instagram_url: seller.instagram_url || "",
    facebook_url: seller.facebook_url || "", tiktok_url: seller.tiktok_url || "",
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try { await apiClient.put("/seller/profile", form); toast.success("Saved"); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="seller-profile-page" className="max-w-2xl space-y-4">
      <h1 className="font-display text-4xl tracking-tighter font-light">{lang === "nl" ? "Profiel" : "Profile"}</h1>
      <a href={`/store/${seller.slug}`} target="_blank" rel="noreferrer" className="text-xs text-terracotta inline-flex items-center gap-1.5"><ExternalLink className="w-3 h-3" /> /store/{seller.slug}</a>
      <Textarea label="Bio NL" value={form.bio_nl} onChange={(v) => upd("bio_nl", v)} />
      <Textarea label="Bio EN" value={form.bio_en} onChange={(v) => upd("bio_en", v)} />
      <F label="Logo URL" value={form.logo_url} onChange={(v) => upd("logo_url", v)} />
      <F label="Banner URL" value={form.banner_url} onChange={(v) => upd("banner_url", v)} />
      <F label="Website" value={form.website_url} onChange={(v) => upd("website_url", v)} />
      <F label="Instagram" value={form.instagram_url} onChange={(v) => upd("instagram_url", v)} />
      <F label="Facebook" value={form.facebook_url} onChange={(v) => upd("facebook_url", v)} />
      <F label="TikTok" value={form.tiktok_url} onChange={(v) => upd("tiktok_url", v)} />
      <button onClick={save} className="btn-primary" data-testid="profile-save"><Save className="w-4 h-4" /> {lang === "nl" ? "Opslaan" : "Save"}</button>
    </div>
  );
};

const F = ({ label, value, onChange, required, type = "text", step, testid }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <input data-testid={testid} type={type} step={step} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);
const Textarea = ({ label, value, onChange }) => (
  <label className="block">
    <div className="text-xs font-label text-ink-muted mb-1">{label}</div>
    <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="border border-black/15 px-3 py-2 text-sm w-full bg-white" />
  </label>
);

// Top-level export — used as a Route element
const Seller = () => (
  <SellerGuard>
    {(seller) => (
      <SellerLayout seller={seller}>
        <Routes>
          <Route index element={<SellerOverview seller={seller} />} />
          <Route path="products" element={<SellerProducts />} />
          <Route path="products/new" element={<SellerProductEdit />} />
          <Route path="products/:id" element={<SellerProductEdit />} />
          <Route path="orders" element={<SellerOrders />} />
          <Route path="earnings" element={<SellerEarnings />} />
          <Route path="profile" element={<SellerProfile seller={seller} />} />
        </Routes>
      </SellerLayout>
    )}
  </SellerGuard>
);

export default Seller;
